// Support search modal — debounced server-side team search + JIT grant.
// Ported from client/client/src/components/SupportSearch.tsx.

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { APP_LANDING_PATH } from '@/shell/navRegistry'
import { X, Search, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import * as keycast from '@/lib/keycastClient'
import type { TeamLookupCandidate } from '@/lib/keycastClient'
import { useTeam } from '@/state/useTeam'
import { recordOpenedTeam } from '@/lib/recentRestaurants'
import { isInternalTeam } from '@/lib/internalTeams'
import {
  MIN_SEARCH_CHARS,
  RESULT_CAP,
  mapSearchError,
  mapGrantError,
  formatAdminEmails,
} from './supportSearchHelpers'

interface SupportSearchProps {
  open: boolean
  onClose: () => void
}

export function SupportSearch({ open, onClose }: SupportSearchProps) {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  const { teams, addSupportSession, selectTeam } = useTeam()

  const [query, setQuery] = useState('')
  const [searching, setSearching] = useState(false)
  const [searchHint, setSearchHint] = useState<string | null>(null)
  const [results, setResults] = useState<TeamLookupCandidate[]>([])
  const [total, setTotal] = useState(0)
  const [grantingTeamId, setGrantingTeamId] = useState<number | null>(null)
  const [grantError, setGrantError] = useState<string | null>(null)

  // Clear state when modal closes
  useEffect(() => {
    if (!open) {
      setQuery('')
      setSearching(false)
      setSearchHint(null)
      setResults([])
      setTotal(0)
      setGrantingTeamId(null)
      setGrantError(null)
    }
  }, [open])

  // Focus input when modal opens
  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 100)
  }, [open])

  // Debounced search
  useEffect(() => {
    if (!open) return
    const trimmed = query.trim()

    if (trimmed.length < MIN_SEARCH_CHARS) {
      setResults([])
      setTotal(0)
      setSearchHint(trimmed.length > 0 ? `Type at least ${MIN_SEARCH_CHARS} characters` : null)
      return
    }

    setSearchHint(null)
    setSearching(true)

    const timer = setTimeout(async () => {
      try {
        const res = await keycast.adminTeamLookup(trimmed)
        setResults(res.results)
        setTotal(res.total)
        if (res.total >= RESULT_CAP) {
          setSearchHint('Refine your search for more results')
        }
      } catch (err) {
        setSearchHint(mapSearchError(err))
        setResults([])
        setTotal(0)
      } finally {
        setSearching(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [query, open])

  // A team the member already belongs to needs no support grant: select it.
  async function handleSwitch(candidate: TeamLookupCandidate) {
    setGrantError(null)
    setGrantingTeamId(candidate.id)
    try {
      recordOpenedTeam(candidate.id)
      await selectTeam(candidate.id)
      onClose()
      navigate(APP_LANDING_PATH)
    } catch (err) {
      setGrantError(mapGrantError(err))
    } finally {
      setGrantingTeamId(null)
    }
  }

  async function handleOpen(candidate: TeamLookupCandidate) {
    setGrantError(null)
    setGrantingTeamId(candidate.id)
    try {
      recordOpenedTeam(candidate.id)
      const res = await keycast.grantSupportAccess(candidate.id)
      // addSupportSession connects the bunker, selects the team (releasing
      // any prior support grant on switch-away, #12), tracks the session,
      // and backfills member data via listTeams.
      await addSupportSession({
        teamId: candidate.id,
        teamName: candidate.name,
        storedKeyPubkey: res.stored_key_pubkey,
        bunkerUrl: res.bunker_url,
      })

      onClose()
      navigate(APP_LANDING_PATH)
    } catch (err) {
      setGrantError(mapGrantError(err))
    } finally {
      setGrantingTeamId(null)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 pt-20" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-xl border border-border bg-background shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search restaurants by name…"
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          />
          {searching && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto">
          {searchHint && (
            <p className="px-4 py-3 text-sm text-muted-foreground">{searchHint}</p>
          )}

          {grantError && (
            <p className="px-4 py-2 text-sm text-destructive">{grantError}</p>
          )}

          {results.filter((c) => !isInternalTeam(c.name)).map((candidate) => {
            const alreadyOpen = teams.some((t) => t.id === candidate.id)
            const isGranting = grantingTeamId === candidate.id

            return (
              <div
                key={candidate.id}
                className="flex items-center justify-between border-b border-border/50 px-4 py-3 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-foreground">{candidate.name}</p>
                  <p className="truncate text-xs text-muted-foreground">
                    {formatAdminEmails(candidate.admin_emails)}
                  </p>
                </div>
                <div className="ml-3 flex shrink-0 items-center gap-2">
                  {!candidate.has_stored_key && (
                    <span className="rounded bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600">
                      Not provisioned
                    </span>
                  )}
                  {alreadyOpen ? (
                    <Button
                      variant="secondary"
                      onClick={() => handleSwitch(candidate)}
                      disabled={grantingTeamId !== null}
                      className="h-7 px-3 text-xs"
                    >
                      {isGranting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                      {isGranting ? 'Switching…' : 'Switch to this'}
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      onClick={() => handleOpen(candidate)}
                      disabled={!candidate.has_stored_key || grantingTeamId !== null}
                      className="h-7 px-3 text-xs"
                    >
                      {isGranting ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                      {isGranting ? 'Opening…' : 'Open'}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}

          {results.length === 0 && query.length >= MIN_SEARCH_CHARS && !searching && !searchHint && (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              No restaurants found for &ldquo;{query}&rdquo;
            </p>
          )}
        </div>

        {/* Footer */}
        {total > 0 && (
          <div className="border-t border-border px-4 py-2 text-xs text-muted-foreground">
            {total} result{total === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </div>
  )
}
